import React from 'react';

const AppointmentActionModal = ({
  isOpen,
  onClose,
  onComplete,
  onCancel,
  onNoShow,
  appointment,
  isLoading
}) => {
  if (!isOpen || !appointment) return null;

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('el-GR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Επιβεβαιωμένο';
      case 'completed': return 'Ολοκληρωμένο';
      case 'in_progress': return 'Σε εξέλιξη';
      case 'cancelled': return 'Ακυρωμένο';
      case 'scheduled': return 'Προγραμματισμένο';
      case 'no_show': return 'Δεν εμφανίστηκε';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10B981';
      case 'completed': return '#3B82F6';
      case 'in_progress': return '#F59E0B';
      case 'cancelled': return '#EF4444';
      case 'scheduled': return '#6B7280';
      case 'no_show': return '#9CA3AF';
      default: return '#4B5563';
    }
  };

  const isActionable = !['completed', 'cancelled', 'no_show'].includes(appointment.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 rounded-xl border shadow-2xl"
        style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: '#374151' }}
        >
          <h3 className="text-lg font-semibold text-white">
            Λεπτομέρειες Ραντεβού
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Appointment info card */}
          <div
            className="rounded-lg p-4 mb-4"
            style={{ backgroundColor: '#111827' }}
          >
            {/* Pet & Owner */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                style={{ backgroundColor: '#10B981' }}
              >
                {appointment.pets?.name?.[0] || '?'}
              </div>
              <div>
                <p className="text-white font-medium text-lg">
                  {appointment.pets?.name}
                </p>
                <p className="text-gray-400 text-sm">
                  {appointment.ownerName || `${appointment.owners?.first_name || ''} ${appointment.owners?.last_name || ''}`.trim()}
                </p>
              </div>
            </div>

            {/* Client Phone (public bookings) */}
            {appointment.client_phone && (
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href={`tel:${appointment.client_phone}`} className="text-gray-300 text-sm hover:text-emerald-400 transition-colors">
                  {appointment.client_phone}
                </a>
              </div>
            )}

            {/* Appointment Time */}
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-300 text-sm">
                {formatDateTime(appointment.appointment_date)}
              </span>
            </div>

            {/* Vet */}
            {(appointment.vetName || appointment.vets) && (
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-gray-300 text-sm">
                  {appointment.vetName || `${appointment.vets?.first_name || ''} ${appointment.vets?.last_name || ''}`.trim()}
                </span>
              </div>
            )}

            {/* Status Badge */}
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">Κατάσταση:</span>
              <span
                className="px-2 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: getStatusColor(appointment.status) }}
              >
                {getStatusLabel(appointment.status)}
              </span>
            </div>
          </div>

          {/* Notes */}
          {appointment.notes && (
            <div className="mb-4">
              <p className="text-gray-400 text-sm">
                <span className="text-gray-500">Σημειώσεις:</span> {appointment.notes}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div
          className="px-6 py-4 border-t space-y-3"
          style={{ borderColor: '#374151' }}
        >
          {isActionable && (
            <div className="space-y-3">
              <div className="flex gap-3">
                {/* Complete Button */}
                <button
                  onClick={() => onComplete(appointment.id)}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#3B82F6' }}
                >
                  {isLoading ? (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <span>Ολοκλήρωση</span>
                </button>

                {/* Cancel Button */}
                <button
                  onClick={() => onCancel(appointment.id)}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#EF4444' }}
                >
                  {isLoading ? (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span>Ακύρωση</span>
                </button>
              </div>

              {/* No Show Button */}
              <button
                onClick={() => onNoShow(appointment.id)}
                disabled={isLoading}
                className="w-full px-4 py-2.5 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#6B7280' }}
              >
                {isLoading ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                )}
                <span>Δεν εμφανίστηκε</span>
              </button>
            </div>
          )}

          {/* Close Button (for completed/cancelled appointments) */}
          {!isActionable && (
            <button
              onClick={onClose}
              className="w-full px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
            >
              Κλείσιμο
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentActionModal;
